// @docs:
//   - docs/core/operations/WEATHER_ADMIN_DASHBOARD.md
//
// Admin › Weather — display helpers: local times, ingest times and table sorting.

import type { WeatherIngestAttempt } from '../../services/api';

export type SortDir = 'asc' | 'desc';

/** "26 Sep 00:35 NZST": the viewer's own time zone (or the one given), always named. */
export function formatLocal(ts: number, opts: { timeZone?: string; locale?: string } = {}): string {
  const parts = new Intl.DateTimeFormat(opts.locale, {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    timeZone: opts.timeZone, timeZoneName: 'short',
  }).formatToParts(new Date(ts));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')} ${get('month')} ${get('hour')}:${get('minute')} ${get('timeZoneName')}`.trim();
}

/** A run id such as 20260925T06Z → its start time in ms (UTC by definition), or null. */
export function runIdToMs(runId: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})Z$/.exec(runId);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4])) : null;
}

type SortValue = string | number | null | undefined;

/** A sorted copy. Empty values (null, undefined, '') go last in either direction. */
export function sortRows<T>(rows: readonly T[], value: (row: T) => SortValue, dir: SortDir): T[] {
  const empty = (v: SortValue) => v === null || v === undefined || v === '';
  return [...rows].sort((a, b) => {
    const va = value(a), vb = value(b);
    if (empty(va) || empty(vb)) return empty(va) === empty(vb) ? 0 : empty(va) ? 1 : -1;
    const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return dir === 'asc' ? c : -c;
  });
}

/** "42 s", "10 min", "1 h 53 min", "2 h". */
export function formatDuration(secs: number): string {
  if (secs < 60) return `${Math.round(secs)} s`;
  const min = Math.round(secs / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** A run that still says `running` this long after it started never recorded its end
 *  (a deploy or a crash stopped the container). */
const RUNNING_FOR_TOO_LONG_S = 12 * 3600;

/** The newest ingest attempt of a run, for the runs table: how long it took (or has been
 *  running), and where the time went. `null` without a record. */
export function ingestTime(attempts: WeatherIngestAttempt[] | undefined, now: number): { text: string; detail: string; secs: number } | null {
  const a = attempts?.length ? attempts[attempts.length - 1] : null;
  if (!a) return null;
  const mode = a.mode.charAt(0).toUpperCase() + a.mode.slice(1);
  const who = a.trigger === 'operator' ? `${mode} by an operator` : `${mode} (cron)`;
  if (a.status === 'running') {
    const secs = Math.max(0, (now - Date.parse(a.startedAt)) / 1000);
    if (secs > RUNNING_FOR_TOO_LONG_S) return { text: 'no end recorded', detail: `${who} · started ${formatDuration(secs)} ago; the container may have restarted`, secs };
    return { text: `running ${formatDuration(secs)}`, detail: `${who} · started ${formatDuration(secs)} ago`, secs };
  }
  const parts: [string, number | null][] = [['download', a.fetchSecs], ['from R2', a.hydrateSecs], ['encode', a.encodeSecs], ['upload', a.uploadSecs]];
  const split = parts.filter(([, v]) => v !== null && v >= 1).map(([k, v]) => `${k} ${formatDuration(v!)}`);
  const secs = a.wallSecs ?? 0;
  return { text: formatDuration(secs), detail: [who, a.status.replace(/_/g, ' '), ...split].join(' · '), secs };
}
